import {
  IonContent,
  IonPage,
  IonText,
  IonIcon,
  IonNavLink,
  IonSkeletonText,
  IonCheckbox,
  useIonToast,
} from '@ionic/react'
import { alertCircleOutline } from 'ionicons/icons'
import { observer } from 'mobx-react-lite'
import { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import { amountString, isLoaded, onCopyContent, ResProductDetail, VALIDATE_STATUS_TRADE } from '../../../../common'
import { CustomSpacer, StyleIonInputNew, StyleIonHeader, TextStyle, FooterButtons } from '../../../../components'
import { DivideLine, DivideLineBold } from '../../../../components/atoms/divide'
import { StoresContext } from '../../../../provider/StoreProvider'
import { LoadingModal } from './loadingModal'

type Props = {
  detailContent: ResProductDetail
  onDismiss: () => void
}

export const SaleOrderModal: React.FC<Props> = observer(({ onDismiss, detailContent }) => {
  const [totalCost, setTotalCost] = useState<number>(0)
  const { commonStore, myStore } = useContext(StoresContext)
  const [count, setCount] = useState<number | undefined>()
  const [validateStatus, setValidateStatus] = useState<VALIDATE_STATUS_TRADE>(VALIDATE_STATUS_TRADE.INPUT)
  const [presentToast] = useIonToast()
  const [inputValues, setInputValues] = useState({
    newCount: 0,
    isReceipt: false,
    receiptPhoneNumber: 0,
  })
  const { newCount, isReceipt, receiptPhoneNumber } = inputValues
  const balance = commonStore.currentBalance.content?.balance ?? 0
  const detailStore = myStore.depositAccountStore
  const purchasedCount = commonStore.getOwnedProduct.content

  useEffect(() => {
    commonStore.currentBalance.load()
    commonStore.getOwnedProduct.load({
      param: { productId: Number(detailContent.id) },
    })
    detailStore.load(undefined, true)
  }, [])

  const onCheckChange = (event: { target: { name: string; checked: boolean } }) => {
    const name = event.target.name
    setInputValues({
      ...inputValues,
      [name]: event.target.checked,
    })
  }
  const onChangeInput = (event: { target: { value: string | number; name: string | number } }) => {
    const { value, name } = event.target
    setInputValues({ ...inputValues, [name]: value })
  }

  useEffect(() => {
    if (isNaN(newCount) || newCount <= 0) {
      setCount(undefined)
      setValidateStatus(VALIDATE_STATUS_TRADE.INPUT)
      return
    }
    setCount(newCount)
    const total = Number(detailContent.activeProductSale.cost) * Number(newCount)
    setTotalCost(total)
    if (total <= 0) {
      setValidateStatus(VALIDATE_STATUS_TRADE.INPUT)
    } else if (total > balance) {
      setValidateStatus(VALIDATE_STATUS_TRADE.BALANCE)
    } else if (purchasedCount && newCount > canPurchasedCount()) {
      setValidateStatus(VALIDATE_STATUS_TRADE.LIMIT_COUNT)
    } else {
      setValidateStatus(VALIDATE_STATUS_TRADE.OKAY)
    }
  }, [newCount])

  const canPurchasedCount = () =>
    Math.min(
      detailContent.activeProductSale.limit - (purchasedCount != null ? purchasedCount.purchasedCount : 0),
      detailContent.activeProductSale.remainCount,
    )

  return (
    <IonPage>
      <StyleIonHeader title={'작품 구매하기'} type={'MODAL'} onDismiss={onDismiss} />
      <IonContent>
        <ProductContainer>
          <ProductInfoContainer>
            <img
              src={detailContent.imageUri}
              alt={detailContent.name}
              onError={({ currentTarget }) => {
                currentTarget.onerror = null
                currentTarget.src = '/img/thumbnail.svg'
              }}
            />
            <div className="product-info">
              <IonText className="artist">{detailContent.artist}</IonText>
              <IonText className="title">{detailContent.name}</IonText>
            </div>
          </ProductInfoContainer>
        </ProductContainer>
        <DivideLineBold />
        <SectionContainer>
          <TextStyle type={'b_16'}>결제정보</TextStyle>
          <BetweenWrap>
            <TextStyle type={'r_13'}>결제 수단</TextStyle>
            <TextStyle type={'b_15'}>예치금</TextStyle>
          </BetweenWrap>
          <CustomSpacer spacing="16px" />
          <DivideLine />
          <BetweenWrap>
            <div className="left_items">
              <TextStyle type={'r_13'}>예치금 총 금액</TextStyle>
              <IonIcon icon={alertCircleOutline}></IonIcon>
            </div>
            <TextStyle type={'b_15'}>
              {commonStore.currentBalance.isLoading && <IonSkeletonText animated={true} style={{ width: '100px' }} />}
              {commonStore.currentBalance.isFailed && '??'}
              {isLoaded(balance) && `${balance.toLocaleString('ko-Kr')} 원`}
            </TextStyle>
          </BetweenWrap>
          <BetweenWrap>
            <div className="left_items">
              <TextStyle type={'r_13'} color={'secondary_main'}>
                거래 가능 금액
              </TextStyle>
              <IonIcon icon={alertCircleOutline}></IonIcon>
            </div>
            <TextStyle type={'b_15'}>
              {commonStore.currentBalance.isLoading && <IonSkeletonText animated={true} style={{ width: '100px' }} />}
              {commonStore.currentBalance.isFailed && '??'}
              {isLoaded(balance) && `${balance.toLocaleString('ko-Kr')} 원`}
            </TextStyle>
          </BetweenWrap>
        </SectionContainer>
        <DivideLineBold />
        <SectionContainer>
          <TextStyle type={'b_16'}>최종 결제 금액</TextStyle>
          <BetweenWrap>
            <TextStyle type={'r_13'}>남은 조각 수</TextStyle>
            <TextStyle type={'b_15'}>
              {detailContent.activeProductSale.remainCount.toLocaleString('ko-Kr')} 개
            </TextStyle>
          </BetweenWrap>
          <BetweenWrap>
            <TextStyle type={'r_13'}>구매 가능 수량</TextStyle>
            <TextStyle type={'b_15'}>{canPurchasedCount().toLocaleString('ko-Kr')} 개</TextStyle>
          </BetweenWrap>
          <BetweenWrap>
            <TextStyle type={'r_13'}>단가</TextStyle>
            <TextStyle type={'b_15'}>{detailContent.activeProductSale.cost.toLocaleString('ko-Kr')} 원</TextStyle>
          </BetweenWrap>
          <BetweenWrap>
            <div className="text_wrap_left">
              <TextStyle type={'r_13'}>구매 수량</TextStyle>
            </div>
            <StyleIonInputNew
              isRightText={true}
              type={'number'}
              inputType={'INPUT'}
              inputStyle={'BOX'}
              placeholder="수량을 입력해주세요"
              onInput={onChangeInput}
              name={'newCount'}
            />
            <div className="text_wrap_right">
              <TextStyle type={'b_15'}>개</TextStyle>
            </div>
          </BetweenWrap>
          <CustomSpacer spacing="16px" bottomLine={true} />
          <CustomSpacer spacing="16px" />
          <BetweenWrap>
            <TextStyle type={'b_16'}>결제 예정 금액</TextStyle>
            <TextStyle type={'b_16'}>{`${amountString(String(totalCost))} 원`}</TextStyle>
          </BetweenWrap>
        </SectionContainer>
        <DivideLineBold />
        <SectionContainer>
          <BetweenWrap2>
            <TextStyle type={'b_16'}>현금영수증</TextStyle>
            <CheckboxWrap>
              <TextStyle type={'b_16'}>신청</TextStyle>
              <IonCheckbox
                className={'check_box'}
                mode="md"
                onIonChange={onCheckChange}
                name="isReceipt"
                checked={isReceipt}
              ></IonCheckbox>
            </CheckboxWrap>
          </BetweenWrap2>
          <CustomSpacer spacing="16px" />
          <StyleIonInputNew
            type={'number'}
            inputType={'INPUT'}
            inputStyle={'BOX'}
            placeholder={'000-0000-0000'}
            onInput={onChangeInput}
            name={'receiptPhoneNumber'}
            disabled={!isReceipt ? true : false}
          />
        </SectionContainer>
      </IonContent>
      <DivideLine />
      <InfoContainer>
        {validateStatus !== VALIDATE_STATUS_TRADE.BALANCE ? (
          <ul>
            <li>주문일로부터 7영업일 이내 주문 취소가 가능합니다.</li>
            <li>해당작품이 트레이드로 넘어간 이후에는 취소가 불가합니다.</li>
            <li>작품 일부만 취소는 불가합니다.</li>
          </ul>
        ) : (
          <p
            onClick={() => {
              onCopyContent(presentToast, {
                content: String(detailStore.content?.account),
                successMsg: '가상 계좌 번호가 복사되었습니다!',
                failureMsg: '가상 계좌 번호 복사에 실패했습니다.',
              })
            }}
          >
            {detailStore.isLoaded && (
              <>
                <span>예치금 충전 계좌 정보</span>
                <span>
                  은행명: {detailStore.content?.bankName ?? '-'} / 예금주: {detailStore.content?.bankOwner ?? '-'}
                </span>
                <span>계좌번호: {detailStore.content?.account ?? '-'}</span>
              </>
            )}
          </p>
        )}
      </InfoContainer>
      {validateStatus === VALIDATE_STATUS_TRADE.OKAY && (
        <IonNavLink
          routerDirection="forward"
          component={() => {
            return (
              <LoadingModal
                type={'SALE'}
                onDismiss={onDismiss}
                saleData={{
                  saleId: detailContent.activeProductSale.id,
                  count: Number(count) ?? 0,
                  isReceipt: isReceipt,
                  receiptPhoneNumber: receiptPhoneNumber,
                }}
              />
            )
          }}
        >
          <FooterButtons
            button={{
              color: 'dark',
              text: validateStatus,
            }}
          />
        </IonNavLink>
      )}
      {validateStatus !== VALIDATE_STATUS_TRADE.OKAY && (
        <FooterButtons
          button={{
            color: 'medium',
            text: validateStatus,
            disabled: true,
          }}
        />
      )}
    </IonPage>
  )
})

const ProductContainer = styled.div`
  padding: 24px 20px;
`

const ProductInfoContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-template-rows: 1fr;
  grid-column-gap: 20px;
  grid-row-gap: 0px;
  img {
    border-radius: 7px;
    min-width: 80px;
    width: 100%;
    aspect-ratio: 1/1;
    object-fit: cover;
  }
  .product-info {
    grid-area: 1 / 1 / 2 / 5;
    display: flex;
    flex-direction: column;
    font-size: 15px;
    .artist {
      white-space: nowrap;
      color: var(--ion-color-primary_light);
      font-size: 13px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .title {
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      word-wrap: break-word;
      font-size: 16px;
      font-weight: 700;
    }
  }
`

const SectionContainer = styled.div`
  padding: 24px 20px;
`

const BetweenWrap = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: space-between;
  .left_items {
    display: flex;
    align-items: center;
    ion-icon {
      margin-left: 6px;
    }
  }
  .text_wrap_left {
    width: 80px;
    margin: auto 0;
  }
  .text_wrap_right {
    width: 20px;
    text-align: end;
    margin: auto 0;
  }
`

const BetweenWrap2 = styled.div`
  display: flex;
  justify-content: space-between;
`

const InfoContainer = styled.footer`
  padding: 8px 20px;
  display: flex;
  flex-direction: column;
  p {
    display: flex;
    flex-direction: column;
    color: var(--ion-color-success_main);
    text-align: center;
    font-size: 14px;
    margin: auto 0;
  }
  ul {
    padding-left: 20px;
    font-size: 11px;
    color: var(--ion-color-primary_light);
    line-height: 21px;
    margin: 5px 0;
  }
`

const CheckboxWrap = styled.div`
  display: flex;
  align-items: center;
  .check_box {
    width: 22px;
    height: 22px;
    margin: 0 0 1px 8px;
    --border-width: 3px;
    --border-color-checked: #000000;
    --checkbox-background-checked: #000000;
    --checkmark-width: 3px;
  }
`
